import {
  FormStageStage,
  TFormState,
} from '@/ui/components/createPurchaseOrderSidebar/CreatePurchaseOrderSidebar';
import { Button, Conditional, Tabs } from '@payaca/ambrosia';

import { getAddressAsString } from '@payaca/helpers/locationHelper';
import { useDeal } from '@payaca/store/hooks/appState';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  PurchaseOrderDeliveryAddressControl,
  Props as PurchaseOrderDeliveryAddressControlProps,
  usePurchaseOrderAddressOptions,
} from '../../purchaseOrderDeliveryAddressControl/PurchaseOrderDeliveryAddressControl';
import {
  AsapFulfillmentOptions,
  CallOffFulfillmentOptions,
} from './ListedFulfillmentOptions';

export interface IProps {
  formStage: FormStageStage;
  projectId: number;
  onAction: () => void;
}
const FulfillmentOptions: FC<IProps> = (props) => {
  const { formStage, projectId, onAction } = props;
  const formMethods = useFormContext<TFormState>();
  const selectedSupplierId = formMethods.watch('supplierId');
  const callOffDate = formMethods.watch('callOffDate');

  const project = useDeal(projectId);
  const defaultAddress = project?.siteAddresses?.[0] || null;

  const [selectedAddress, setSelectedAddress] = useState<
    PurchaseOrderDeliveryAddressControlProps['value']
  >({
    deliveryAddressId: defaultAddress?.address.id,
    latitude: defaultAddress?.address.latitude || undefined,
    longitude: defaultAddress?.address.longitude || undefined,
  });

  const { options } = usePurchaseOrderAddressOptions({
    projectId,
    supplierId: selectedSupplierId,
  });

  const hasSelectedAddress =
    !!selectedAddress?.deliveryAddressId ||
    !!(
      selectedAddress.newDeliveryAddress &&
      !!Object.keys(selectedAddress.newDeliveryAddress).length
    );

  const onSelectDelivery = (branchId?: string, branchName?: string) => {
    const existingAddress = options.find(
      (o) => o.value === selectedAddress.deliveryAddressId
    );
    if (existingAddress) {
      formMethods.setValue('delivery', {
        deliveryAddressId: existingAddress?.value,
        newDeliveryAddress: undefined,
        fullLocalAddress: existingAddress?.label,
        branchId: branchId,
        branchName: branchName,
      });
    } else if (selectedAddress.newDeliveryAddress) {
      formMethods.setValue('delivery', {
        deliveryAddressId: undefined,
        newDeliveryAddress: {
          line1: selectedAddress.newDeliveryAddress.line1 ?? undefined,
          line2: selectedAddress.newDeliveryAddress.line2 ?? undefined,
          city: selectedAddress.newDeliveryAddress.city ?? undefined,
          administrativeArea:
            selectedAddress.newDeliveryAddress.administrativeArea ?? undefined,
          postcode: selectedAddress.newDeliveryAddress.postcode ?? undefined,
          country: selectedAddress.newDeliveryAddress.country ?? undefined,
        },
        fullLocalAddress: getAddressAsString(
          selectedAddress.newDeliveryAddress
        ),
        branchId: branchId,
        branchName: branchName,
      });
    }
    // clear collection field
    formMethods.setValue('collection', undefined);
    // clear additional notes (not an option for ACO delivery)
    formMethods.setValue('additionalNotes', undefined);
  };

  const onSelectCollection = (branchId: string, branchName: string) => {
    formMethods.setValue('collection', {
      branchId: branchId,
      branchName: branchName,
    });
    // clear delivery field
    formMethods.setValue('delivery', undefined);
  };

  return (
    <div
      className={clstx(
        'z-10 overflow-auto flex flex-col h-full transition-colors duration-700 bg-gray-50 border-l',
        formStage === 'DIRECT_PURCHASE_ORDER' && 'bg-white'
      )}
    >
      <div
        className={clstx(
          'p-3.5 z-10 bg-gray-50 transition-colors duration-700 sticky top-0 flex flex-col gap-2',
          formStage === 'DIRECT_PURCHASE_ORDER' && 'bg-white',
          scrollY > 0 && 'shadow'
        )}
      >
        <Conditional
          condition={formStage === 'FULFILLMENT_OPTIONS' || !hasSelectedAddress}
        >
          <span className="block mb-2 font-medium">
            Collection/delivery address
          </span>
          <PurchaseOrderDeliveryAddressControl
            supplierId={selectedSupplierId}
            projectId={projectId}
            onChange={setSelectedAddress}
            value={selectedAddress}
            disableManualEdit
            canRevertToExisting
          />
        </Conditional>
        <Conditional condition={!hasSelectedAddress}>
          <span className="mt-2">
            Please select a collection/delivery address
          </span>
        </Conditional>
        <Conditional condition={formStage !== 'FULFILLMENT_OPTIONS'}>
          <Button
            variant="link"
            onClick={() => {
              onAction();
            }}
          >
            Edit collection/delivery address
          </Button>
        </Conditional>

        <Conditional condition={hasSelectedAddress}>
          {/* date */}
          {formStage !== 'FULFILLMENT_OPTIONS' ? (
            <div>
              {!callOffDate ? (
                <AsapFulfillmentOptions
                  formStage={formStage}
                  onSelectDelivery={onSelectDelivery}
                  onSelectCollection={onSelectCollection}
                  hasSelectedAddress={hasSelectedAddress}
                  onAction={onAction}
                  selectedAddress={selectedAddress}
                  projectId={projectId}
                />
              ) : (
                <CallOffFulfillmentOptions
                  formStage={formStage}
                  onSelectDelivery={onSelectDelivery}
                  onSelectCollection={onSelectCollection}
                  hasSelectedAddress={hasSelectedAddress}
                  selectedAddress={selectedAddress}
                  onAction={onAction}
                />
              )}
            </div>
          ) : (
            <div>
              <span className="block mb-2 font-medium mt-2">
                Collection/delivery date
              </span>
              <Tabs
                variant="segment"
                defaultIndex={0}
                onChange={(index) => {
                  if (index === 0) {
                    formMethods.setValue('type', 'direct-asap');
                    formMethods.setValue('callOffDate', undefined);
                  } else {
                    formMethods.setValue('fulfillmentOptionId', undefined);
                    formMethods.setValue('toDate', undefined);
                    formMethods.setValue('fromDate', undefined);
                    formMethods.setValue('type', 'direct-aco');
                  }
                }}
              >
                <Tabs.Panel
                  label="ASAP"
                  render={() => {
                    return (
                      <AsapFulfillmentOptions
                        formStage={formStage}
                        onSelectDelivery={onSelectDelivery}
                        onSelectCollection={onSelectCollection}
                        hasSelectedAddress={hasSelectedAddress}
                        onAction={onAction}
                        selectedAddress={selectedAddress}
                        projectId={projectId}
                      />
                    );
                  }}
                />
                <Tabs.Panel
                  label="Future date"
                  render={() => {
                    return (
                      <CallOffFulfillmentOptions
                        formStage={formStage}
                        onSelectDelivery={onSelectDelivery}
                        onSelectCollection={onSelectCollection}
                        hasSelectedAddress={hasSelectedAddress}
                        selectedAddress={selectedAddress}
                        onAction={onAction}
                      />
                    );
                  }}
                />
              </Tabs>
            </div>
          )}
        </Conditional>
      </div>
    </div>
  );
};

export default FulfillmentOptions;
